/**
 * Site-group (plant) scope routes — the pages that mount inside
 * SiteGroupLayout for a single plant: the structure directory, the plant's
 * map, the user/support directories, plant settings, sales/returns contact
 * forms, inventory, and
 * the plant report suite (solar, load schedules, alarm summary, disabled
 * loads, digital maintenance, fixture, proof-of-performance, maintenance).
 * The plant-context RMA routes render here at their original position via
 * the returns bundle. The contact-form routes are gated on the combined
 * sales+returns exclude flags.
 *
 * Contains:
 * - siteGroupRoutes — a fragment of <Route> elements for the composer
 * - OfflineReportRedirect — redirect helper for the offline-alarms report
 */
import React from 'react';
import { Navigate, Route, useParams } from 'react-router-dom';

import SiteGroupLayout from '@sophocles/components/Layout/SiteGroupLayout';
import {
  determineCanViewUsersInPlant,
  determineInventoryAccess,
  determineIsStaff,
  determineSpecificPlantAccess,
} from '@sophocles/router/checkCredentials';
import {
  customerInventoryPath,
  customerMaintenanceReportPath,
  customerOfflineReportRedirectPath,
  siteGroupContactCreatePath,
  siteGroupContactPath,
  siteGroupInventoryPath,
  siteGroupMaintenanceReportPath,
  siteGroupPath,
  siteGroupProofOfPerformancePath,
  siteGroupReportsPath,
  siteGroupSettingsPath,
  siteGroupSettingsSectionPath,
  siteGroupTicketCreatePath,
} from '@sophocles/router/paths';
import RequireAuth from '@sophocles/router/RequireAuth';
import { returnsRoutes } from '@sophocles/router/routes/returns';
import CustomerInventoryView from '@sophocles/views/inventory/CustomerInventoryView';
import CustomerMapView from '@sophocles/views/map/CustomerMapView';
import SiteGroupInventoryView from '@sophocles/views/inventory/SiteGroupInventoryView';
import DigitalMaintenanceReport from '@sophocles/views/reports/DigitalMaintenanceReportView';
import DisabledPanelsReportPage from '@sophocles/views/reports/DisabledPanelsReportView';
import FixtureReportPage from '@sophocles/views/reports/FixtureReportView';
import MaintenanceReportView from '@sophocles/views/reports/MaintenanceReportView';
import PlantRelayLoadsPage from '@sophocles/views/reports/PlantRelayLoadsReportView';
import SiteGroupAlarmLiveReport from '@sophocles/views/reports/SiteGroupAlarmLiveReportView';
import SiteGroupReportsView from '@sophocles/views/reports/SiteGroupReportsView';
import SolarReportView from '@sophocles/views/reports/SolarReportView';
import ContactFormView from '@sophocles/views/sales/ContactFormView';
import SiteGroupSettingsPage from '@sophocles/views/settings/SiteGroupSettingsViewV2';
import UserSettingsPage from '@sophocles/views/settings/UserSettingsViewV2';
import SiteGroupSupportView from '@sophocles/views/siteGroups/SiteGroupSupportView';
import ProofOfPerformanceView from '@sophocles/views/sites/ProofOfPerformanceView';
import StructureGridView from '@sophocles/views/sites/SiteGridView';
import { NewTicketViewPlant } from '@sophocles/views/tickets/NewTicketView';
import TicketGridView from '@sophocles/views/tickets/TicketGridView';
import CreateUserView from '@sophocles/views/users/CreateUserView';
import EditPasswordView from '@sophocles/views/users/EditPasswordView';
import SiteGroupUserGridView from '@sophocles/views/users/SiteGroupUserGridView';

export function OfflineReportRedirect() {
  const { cust_id } = useParams();
  return <Navigate to={`/c/${cust_id}/reports/maintenance?alarms=offline`} replace />;
}

export const siteGroupRoutes = (
  <>
    <Route
      path={siteGroupPath.pattern}
      element={<RequireAuth component={SiteGroupLayout} onEnter={determineSpecificPlantAccess} />}
    >
      <Route
        path="structures"
        element={<RequireAuth component={StructureGridView} onEnter={determineSpecificPlantAccess} />}
      />
      {/* The customer map, fixed to this plant: same view, no group control. */}
      <Route path="map" element={<RequireAuth component={CustomerMapView} onEnter={determineSpecificPlantAccess} />} />
      <Route
        path="map/site/:site_id"
        element={<RequireAuth component={CustomerMapView} onEnter={determineSpecificPlantAccess} />}
      />
      <Route
        path="users"
        element={<RequireAuth component={SiteGroupUserGridView} onEnter={determineCanViewUsersInPlant} />}
      />
      <Route
        path="support"
        element={<RequireAuth component={SiteGroupSupportView} onEnter={determineSpecificPlantAccess} />}
      />
      <Route path="support/tickets" element={<RequireAuth component={TicketGridView} onEnter={determineIsStaff} />} />
      <Route
        path="users/new"
        element={<RequireAuth component={CreateUserView} onEnter={determineCanViewUsersInPlant} />}
      />
      <Route
        path="users/:user_id"
        element={<RequireAuth component={UserSettingsPage} onEnter={determineCanViewUsersInPlant} />}
      />
      <Route
        path="users/:user_id/settings"
        element={<RequireAuth component={UserSettingsPage} onEnter={determineCanViewUsersInPlant} />}
      />
      <Route
        path="users/:user_id/settings/:section"
        element={<RequireAuth component={UserSettingsPage} onEnter={determineCanViewUsersInPlant} />}
      />
      <Route
        path="users/:user_id/manualpassword"
        element={<RequireAuth component={EditPasswordView} onEnter={determineIsStaff} />}
      />
    </Route>
    <Route
      path={siteGroupSettingsPath.pattern}
      element={<RequireAuth component={SiteGroupSettingsPage} onEnter={determineSpecificPlantAccess} />}
    />
    <Route
      path={siteGroupSettingsSectionPath.pattern}
      element={<RequireAuth component={SiteGroupSettingsPage} onEnter={determineSpecificPlantAccess} />}
    />
    {!(__EXCLUDE_SALES__ && __EXCLUDE_RETURNS__) && (
      <>
        <Route
          path={siteGroupContactCreatePath.pattern}
          element={<RequireAuth component={ContactFormView} onEnter={determineSpecificPlantAccess} />}
        />
        <Route
          path={siteGroupContactPath.pattern}
          element={<RequireAuth component={ContactFormView} onEnter={determineSpecificPlantAccess} />}
        />
      </>
    )}
    {/* CUSTOMER REPORTS */}
    <Route
      path={customerInventoryPath.pattern}
      element={<RequireAuth component={CustomerInventoryView} onEnter={determineInventoryAccess} />}
    />
    <Route
      path={siteGroupInventoryPath.pattern}
      element={<RequireAuth component={SiteGroupInventoryView} onEnter={determineInventoryAccess} />}
    />
    <Route path={customerOfflineReportRedirectPath.pattern} element={<OfflineReportRedirect />} />

    {/* SITE GROUP REPORTS */}

    <Route
      path={siteGroupReportsPath.pattern}
      element={<RequireAuth component={SiteGroupLayout} onEnter={determineSpecificPlantAccess} />}
    >
      <Route index element={<RequireAuth component={SiteGroupReportsView} onEnter={determineSpecificPlantAccess} />} />
      <Route
        path="dailysolar"
        element={
          <RequireAuth
            component={SolarReportView}
            componentProps={{ period: 'day' }}
            onEnter={determineSpecificPlantAccess}
          />
        }
      />
      <Route
        path="weeklysolar"
        element={
          <RequireAuth
            component={SolarReportView}
            componentProps={{ period: 'week' }}
            onEnter={determineSpecificPlantAccess}
          />
        }
      />
      <Route
        path="monthlysolar"
        element={
          <RequireAuth
            component={SolarReportView}
            componentProps={{ period: 'month' }}
            onEnter={determineSpecificPlantAccess}
          />
        }
      />
      <Route
        path="yearlysolar"
        element={
          <RequireAuth
            component={SolarReportView}
            componentProps={{ period: 'year' }}
            onEnter={determineSpecificPlantAccess}
          />
        }
      />
      <Route
        path="loadschedules"
        element={<RequireAuth component={PlantRelayLoadsPage} onEnter={determineSpecificPlantAccess} />}
      />
      <Route
        path="alarmlivesummary"
        element={<RequireAuth component={SiteGroupAlarmLiveReport} onEnter={determineSpecificPlantAccess} />}
      />
      <Route
        path="disabledloads"
        element={<RequireAuth component={DisabledPanelsReportPage} onEnter={determineSpecificPlantAccess} />}
      />
      <Route
        path="digitalmaintenance"
        element={<RequireAuth component={DigitalMaintenanceReport} onEnter={determineSpecificPlantAccess} />}
      />
      <Route
        path="fixture"
        element={<RequireAuth component={FixtureReportPage} onEnter={determineSpecificPlantAccess} />}
      />
    </Route>
    <Route
      path={customerMaintenanceReportPath.pattern}
      element={<RequireAuth component={MaintenanceReportView} onEnter={determineSpecificPlantAccess} />}
    />
    <Route
      path={siteGroupProofOfPerformancePath.pattern}
      element={<RequireAuth component={ProofOfPerformanceView} onEnter={determineSpecificPlantAccess} />}
    />
    <Route
      path={siteGroupMaintenanceReportPath.pattern}
      element={<RequireAuth component={MaintenanceReportView} onEnter={determineSpecificPlantAccess} />}
    />
    {/* Plant-context RMA routes (part of returnsRoutes) */}
    {returnsRoutes}

    {/* TICKETS */}

    <Route
      path={siteGroupTicketCreatePath.pattern}
      element={<RequireAuth component={NewTicketViewPlant} onEnter={determineIsStaff} />}
    />
  </>
);
